import { observable, reaction } from "mobx";
import { QuizObjectCard } from "./quiz-object-card";
import { AUTOSAVE_DELAY } from "../../constants/editor";
import QuizObjectPatchable from "./quiz-object-patchable";

class QuizObjectCardTable extends QuizObjectPatchable {
    /**
     *  @property {Object[]} values A Set of values is a column of values for a single column.
     */
    @observable public _id: string | undefined = undefined;
    @observable public slug: string = "";
    @observable public name: string = "";
    @observable public cards: QuizObjectCard[] = [];
    @observable public subjectHashKey: string = "";
    rootStore: any;

    constructor(subject: any, quizObjectId: any, quizObjectStatus: Map<string, any>, tableData?: any) {
      super(quizObjectId, quizObjectStatus);

        this.subjectHashKey = `${subject.user}/${subject.slug}`;
        if (tableData) {
            this._id = tableData._id;
            this.slug = tableData.slug;
            this.name = tableData.name;
            if (tableData.cards) {
                this.cards = tableData.cards.map((card: any) => {
                    if (typeof card === "string") {
                        return new QuizObjectCard(this, {
                            _id: card,
                        });
                    }
                    return new QuizObjectCard(this, card);
                });
            } else {
                this.cards = [];
            }
        } else {
            const card1 = new QuizObjectCard(this);
            this.cards.push(card1);
        }

        reaction(
            () => {
                return JSON.stringify(this.cardTableJSON());
            },
            () => {
                if (this.cardTableReady) {
                    this.persist();
                }
            },
            { delay: AUTOSAVE_DELAY }
        );
    }

    /**
     * When we first instantiate a card table, it will have no title and it's cards will have no titles.
     * We should not save the card table
     */
    get cardTableReady(): boolean {
        return !!(
            this.name &&
            this.name.length > 0 &&
            this.cards &&
            this.cards.length > 0 &&
            this.cards[0] &&
            this.cards[0].title &&
            this.cards[0].title.length > 0
        );
    }


    public addRow() {
        this.cards.push(new QuizObjectCard(this, this.subjectHashKey));
    }

    public getRows() {
        return this.cards;
    }

    public getNextOrderValue() {
        let next = 1;
        this.cards.forEach(card => {
            if (card.order !== undefined && next <= card.order) {
                next = card.order + 1;
            }
        });
        return next;
    }

    setName(name: string) {
        this.name = name;
    }

    /*
     *   This is the public api to remove a card table from the quiz object.
     */
    async remove() {
        await this._patchDelete({
            deleteCardTable: this.cardTableJSON(),
        });
    }

    /*
     *   This is the public api to restore a deleted card table from the quiz object.
     */
    async restore() {
        await this._patchRestore({
            card_table: this.cardTableJSON()
        });
    }

    /*
     *   This is the public api to persist a card table to the quiz object.
     */
    async persist() {
        let _this = this;
        await this._patchPersist(
            {
                card_table: this.cardTableJSON(),
            },
            "card_table",
            (res: any) => {
                const cardTable = _this.findCardTable(
                    res.quiz_object.card_tables,
                );

                if (cardTable) {
                    let newCards = cardTable.cards.map((card: any, i: number) => {
                        if (typeof card == "string") {
                            _this.cards[i]._id = card;
                        } else {
                            _this.cards[i]._id = card._id;

                        }
                    });
                }
            }
        );
    }

    /**
     * This omits certain fields, like status and subject hash key when
     * 1: Considering whether or not an object has changed
     * 2: Serializing the object to send to the backend.
     */
    cardTableJSON() {
        const { status, subjectHashKey, ...retval } = this;
        return retval;
    }

    findCardTable(cardTables: Array<any>): any {
        let retVal;
        cardTables.forEach(cardTable => {
            if ((cardTable._id === this._id) || (cardTable.name === this.name)) {
                retVal = cardTable;
            }
        });
        return retVal;
    }
}

export { QuizObjectCardTable };
